/**
 * ConsultaAnalysisView — renders the structured consulta analysis from the LLM.
 *
 * Supports three payload shapes:
 *   v4: ConsultaReport (method_stages with slug keys, grade, flags_criticas, etc.)
 *   v3-legacy: { score_total, etapas, classificacao, ... } — old format
 *   clinical-legacy: { queixa_principal, hipoteses_diagnosticas, ... } — clinical v2
 *
 * Falls back to raw pre-formatted text when none match.
 */
import { useEffect, useState } from "react";
import { isNoConfiguredProviderError, showNoConfiguredProviderToast } from "@/lib/edgeFnError";
import {
  CheckCircle,
  XCircle,
  Warning,
  Lightning,
  CaretDown,
  Brain,
  Quotes,
  ArrowRight,
  Spinner,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
  ConsultaReport,
  AnalysisGrade,
  EtapaSlug,
  MethodStagesUnified,
  FlagCritica,
  AcaoCorretiva,
  Gargalo,
  NarrativeItem,
  EneatypeDetected,
  EneatypeAdaptation,
  CampoSemantico,
  TermoSemantico,
  PersonalidadePaciente,
} from "@/lib/analysis/types";
import { cn } from "@/lib/utils";
import { useGenerateRoteiro } from "@/features/roteiros/hooks/useGenerateRoteiro";
import type { RoteiroType, ObjetivoRoteiro } from "@/features/roteiros/types";

// ─── Stage order / labels ─────────────────────────────────────────────────────

const ETAPA_LABELS_V4: Record<EtapaSlug, string> = {
  conexao:             "1 · Conexão e Autoridade",
  perguntas_iniciais:  "2 · 3 Perguntas Iniciais",
  investigacao_dor:    "3 · Investigação de Dor",
  diagnostico_clinico: "4 · Diagnóstico Clínico",
  oferta:              "5 · Oferta",
  fechamento:          "6 · Fechamento",
  geral:               "Geral",
};

const ETAPA_ORDER_V4: EtapaSlug[] = [
  "conexao", "perguntas_iniciais", "investigacao_dor",
  "diagnostico_clinico", "oferta", "fechamento"
];

const ETAPA_SHORT: Record<string, string> = {
  conexao:             "Conexão",
  perguntas_iniciais:  "Perguntas",
  investigacao_dor:    "Investigação",
  diagnostico_clinico: "Diagnóstico",
  oferta:              "Oferta",
  fechamento:          "Fechamento",
  geral:               "Geral",
};

// ─── Grade colors ─────────────────────────────────────────────────────────────

const GRADE_META: Record<AnalysisGrade, { label: string; cls: string }> = {
  excelente:    { label: "Excelente",    cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  bom:          { label: "Bom",          cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  intermediario:{ label: "Treinar",      cls: "bg-warning/15 text-yellow-400 border-warning/30" },
  fraco:        { label: "Fraco",        cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  critico:      { label: "Recomeçar",    cls: "bg-destructive/15 text-red-400 border-destructive/30" },
};

// ─── Payload type guards ──────────────────────────────────────────────────────

type AnalysisV3Legacy = {
  score_total: number;
  classificacao: string;
  etapas: Record<string, unknown>;
  [k: string]: unknown;
};

type ClinicalLegacy = {
  queixa_principal?: string;
  historico_relevante?: string;
  exame_fisico?: string | null;
  hipoteses_diagnosticas?: string[];
  plano_terapeutico?: string[];
  alertas_clinicos?: string[];
  resumo_executivo?: string;
  erro?: string;
  mensagem?: string;
};

type ErrorPayload = { erro: string; mensagem?: string };

type ParsedPayload =
  | { kind: "v4";       data: ConsultaReport }
  | { kind: "v3";       data: AnalysisV3Legacy }
  | { kind: "clinical"; data: ClinicalLegacy }
  | { kind: "error";    data: ErrorPayload }
  | null;

function tryParse(raw: unknown): ParsedPayload {
  if (raw == null) return null;

  let candidate: unknown = raw;

  if (typeof raw === "string") {
    const cleaned = raw.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
    try {
      candidate = JSON.parse(cleaned);
    } catch {
      return null;
    }
  }

  if (!candidate || typeof candidate !== "object") return null;

  const o = candidate as Record<string, unknown>;

  // Error payload
  if ("erro" in o && typeof o.erro === "string") {
    return { kind: "error", data: { erro: o.erro as string, mensagem: o.mensagem as string | undefined } };
  }

  // v4: method_stages with slug keys (conexao, perguntas_iniciais, etc.)
  if ("method_total" in o && "method_stages" in o && o.method_stages && typeof (o.method_stages as Record<string, unknown>).conexao === "object") {
    return { kind: "v4", data: o as unknown as ConsultaReport };
  }

  // v3 legacy: score_total + etapas
  if ("score_total" in o && "etapas" in o) {
    return { kind: "v3", data: o as unknown as AnalysisV3Legacy };
  }

  // Clinical legacy v2: queixa_principal, hipoteses_diagnosticas, etc.
  const looksLikeClinical =
    "queixa_principal" in o ||
    "hipoteses_diagnosticas" in o ||
    "plano_terapeutico" in o ||
    "resumo_executivo" in o;

  if (looksLikeClinical) {
    return { kind: "clinical", data: o as unknown as ClinicalLegacy };
  }

  return null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stageColors(score: number): { bar: string; text: string } {
  if (score >= 8) return { bar: "bg-emerald-500", text: "text-emerald-400" };
  if (score >= 5) return { bar: "bg-amber-500",   text: "text-amber-400"  };
  return               { bar: "bg-rose-500",   text: "text-rose-400"   };
}

function extractText(item: string | NarrativeItem): string {
  return typeof item === "string" ? item : item.text;
}

function extractEtapa(item: string | NarrativeItem): string | null {
  return typeof item === "string" ? null : item.etapa ?? null;
}

// ─── Small atoms ─────────────────────────────────────────────────────────────

function EtapaTag({ etapa, className }: { etapa: string; className?: string }) {
  return (
    <span className={cn(
      "inline-flex items-center text-[10px] font-mono text-muted-foreground/70 bg-muted/30 px-1.5 py-0.5 rounded border border-border/30 whitespace-nowrap shrink-0",
      className
    )}>
      {ETAPA_SHORT[etapa] ?? etapa}
    </span>
  );
}

// ─── V4 Stage row (collapsible) ───────────────────────────────────────────────

function StageRowV4({
  slug: _slug, label, stageEval, isCritical, index,
}: {
  slug: EtapaSlug;
  label: string;
  stageEval: MethodStagesUnified[keyof MethodStagesUnified];
  isCritical: boolean;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const { bar, text } = stageColors(stageEval.score);
  const pct = (stageEval.score / 10) * 100;
  const hasDetail =
    stageEval.criterios_atingidos.length > 0 ||
    stageEval.criterios_perdidos.length > 0 ||
    Boolean(stageEval.evidencia);

  return (
    <div
      className={cn(
        "rounded-lg border border-border/40 overflow-hidden",
        isCritical && "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((e) => !e)}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition",
          hasDetail && "hover:bg-muted/20 cursor-pointer",
          !hasDetail && "cursor-default",
        )}
      >
        <span className="text-xs font-mono text-muted-foreground w-4 shrink-0 select-none">
          {index + 1}
        </span>
        <span className="text-sm font-medium flex-1 min-w-0">
          <span className={isCritical ? "text-amber-300" : "text-foreground"}>{label}</span>
          {isCritical && (
            <span className="ml-2 text-[10px] font-normal text-amber-400/80 uppercase tracking-wider">
              etapa crítica
            </span>
          )}
        </span>
        <div className="w-16 h-1.5 bg-muted/40 rounded-full overflow-hidden shrink-0">
          <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${pct}%` }} />
        </div>
        <span className={cn("text-sm font-bold tabular-nums w-9 text-right shrink-0", text)}>
          {stageEval.score}/10
        </span>
        {hasDetail ? (
          <CaretDown weight="bold" className={cn("w-3 h-3 text-muted-foreground transition-transform duration-150 shrink-0", expanded && "rotate-180")} />
        ) : (
          <span className="w-3 shrink-0" />
        )}
      </button>

      {expanded && hasDetail && (
        <div className="px-4 pb-3 border-t border-border/30 pt-3 space-y-2.5">
          {stageEval.criterios_atingidos.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 font-medium">Atingido</p>
              <ul className="space-y-1">
                {stageEval.criterios_atingidos.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle weight="fill" className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {stageEval.criterios_perdidos.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-rose-400 mb-1.5 font-medium">Ausente / Fraco</p>
              <ul className="space-y-1">
                {stageEval.criterios_perdidos.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <XCircle weight="fill" className="w-3 h-3 text-rose-400 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {stageEval.evidencia && (
            <div className="mt-2 border-l-2 border-border/40 pl-3 bg-muted/20 rounded-r py-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-0.5">Evidência</p>
              <p className="text-xs text-foreground/80 italic leading-relaxed">"{stageEval.evidencia}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── V4 Flags block ───────────────────────────────────────────────────────────

const FLAG_LABELS: Record<string, string> = {
  PULO_DESEJO:        "Pulou direto para a oferta sem despertar desejo",
  ABERTURA_GENERICA:  "Abertura sem personalização nem autoridade",
  SEM_SIGNIFICADO:    "Não conectou o procedimento ao significado emocional",
  DOMINANCIA_PERDIDA: "Perdeu o controle da condução da consulta",
  URGENCIA_INVENTADA: "Criou urgência artificial sem embasamento real",
  MICRO_SOLUCAO:      "Ofereceu solução pequena demais para o problema apresentado",
  PRECO_PRECOCE:      "Mencionou o preço antes de construir o valor",
  PROLIXIDADE:        "Falou demais e perdeu a atenção do paciente",
};

function FlagsBlock({ flags }: { flags: FlagCritica[] }) {
  const detected = flags.filter(f => f.detectada);
  if (detected.length === 0) return null;

  return (
    <Card className="p-4 bg-rose-500/5 border-2 border-rose-500/40">
      <header className="mb-3 flex items-center gap-2">
        <Warning weight="fill" className="w-5 h-5 text-rose-400" />
        <h3 className="text-sm font-semibold text-rose-300">Alertas que custaram a venda</h3>
        <span className="ml-auto text-xs bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-full px-2 py-0.5">
          {detected.length}
        </span>
      </header>
      <div className="space-y-2">
        {detected
          .sort((a, b) => {
            const order = { alto: 0, medio: 1, baixo: 2 };
            return order[a.impacto] - order[b.impacto];
          })
          .map((flag, i) => {
            const impactoCls = flag.impacto === "alto"
              ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
              : flag.impacto === "medio"
              ? "border-amber-500/30 bg-amber-500/8 text-amber-300"
              : "border-border/30 bg-muted/20 text-muted-foreground";
            const pillCls = flag.impacto === "alto"
              ? "bg-rose-500/20 text-rose-400 border-rose-500/30"
              : flag.impacto === "medio"
              ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
              : "text-muted-foreground";
            return (
              <div key={i} className={cn("rounded-lg border px-4 py-3", impactoCls)}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">{FLAG_LABELS[flag.flag] ?? flag.flag.replace(/_/g, " ")}</span>
                  <span className={cn("ml-auto text-[10px] border rounded-full px-2 py-0.5", pillCls)}>
                    {flag.impacto === "alto" ? "Alto impacto" : flag.impacto === "medio" ? "Médio impacto" : "Baixo impacto"}
                  </span>
                </div>
                {flag.evidencia && (
                  <p className="text-xs italic border-l-2 border-current/40 pl-3 leading-relaxed opacity-80">
                    "{flag.evidencia}"
                  </p>
                )}
              </div>
            );
          })}
      </div>
    </Card>
  );
}

// ─── V4 Gargalo block ─────────────────────────────────────────────────────────

function GargaloBlock({ gargalo }: { gargalo: Gargalo }) {
  return (
    <Card className="p-4 bg-amber-500/5 border-amber-500/30">
      <header className="mb-2 flex items-center gap-2">
        <Warning weight="duotone" className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-foreground">Gargalo principal</h3>
        <span className="ml-2 text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">
          {ETAPA_LABELS_V4[gargalo.etapa] ?? ETAPA_SHORT[gargalo.etapa] ?? gargalo.etapa.replace(/_/g, " ")}
        </span>
      </header>
      <p className="text-sm text-foreground/90 leading-relaxed">{gargalo.descricao}</p>
      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
        <span className="text-amber-400/80 font-medium">Impacto: </span>
        {gargalo.impacto_na_conversao}
      </p>
    </Card>
  );
}

// ─── V4 Ações corretivas ──────────────────────────────────────────────────────

function AcoesBlock({ acoes }: { acoes: AcaoCorretiva[] }) {
  if (!acoes.length) return null;

  return (
    <Card className="overflow-hidden bg-primary/5 border-primary/25">
      <div className="px-4 py-3 border-b border-primary/15 flex items-center gap-2">
        <Lightning weight="duotone" className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Ações corretivas</h3>
      </div>
      <div className="divide-y divide-primary/10">
        {acoes.map((acao, i) => (
          <div key={i} className={cn("px-4 py-3", i === 0 && "bg-primary/5")}>
            <div className="flex items-start gap-3 border-b border-primary/10 pb-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-primary/20 text-primary font-bold text-sm flex items-center justify-center shrink-0">
                {acao.prioridade}
              </span>
              <div className="flex-1 min-w-0">
                {i === 0 && (
                  <span className="text-[10px] uppercase tracking-wider text-primary font-bold">PRIORIDADE</span>
                )}
                <p className="text-sm font-semibold text-foreground leading-tight">{acao.problema}</p>
                <span className="text-[10px] font-mono text-muted-foreground">
                  {ETAPA_LABELS_V4[acao.etapa] ?? ETAPA_SHORT[acao.etapa] ?? acao.etapa}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Lightning weight="bold" className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground leading-relaxed">{acao.acao}</p>
                {acao.impacto_estimado && (
                  <p className="text-xs text-emerald-400 mt-1">{acao.impacto_estimado}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── V4 Narrative section ─────────────────────────────────────────────────────

function NarrativeSection({
  title, items, tone, icon: Icon,
}: {
  title: string;
  items: Array<string | NarrativeItem>;
  tone: "emerald" | "amber" | "primary";
  icon: typeof CheckCircle;
}) {
  if (!items?.length) return null;
  const toneCls =
    tone === "emerald" ? "text-emerald-400" : tone === "amber" ? "text-amber-400" : "text-primary";

  return (
    <Card className="p-4 bg-card/80 border-border/50">
      <header className="mb-3 flex items-center gap-2">
        <Icon weight="duotone" className={cn("w-4 h-4", toneCls)} />
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </header>
      <div className="space-y-2">
        {items.map((item, i) => {
          const text  = extractText(item);
          const etapa = extractEtapa(item);
          return (
            <div key={i} className={cn(
              "rounded-lg border px-4 py-3 flex items-start gap-3",
              tone === "emerald" ? "border-emerald-500/20 bg-emerald-500/5" :
              tone === "amber"   ? "border-amber-500/20 bg-amber-500/5" :
                                   "border-primary/20 bg-primary/5"
            )}>
              <Icon weight="duotone" className={cn("w-5 h-5 shrink-0 mt-0.5", toneCls)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-relaxed">{text}</p>
                {etapa && etapa !== "geral" && <EtapaTag etapa={etapa} className="mt-1.5" />}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── V4 render ───────────────────────────────────────────────────────────────

function EneatypeBlock({
  detected,
  adaptation,
}: {
  detected?: EneatypeDetected;
  adaptation?: EneatypeAdaptation;
}) {
  if (!detected && !adaptation) return null;

  const confColor =
    detected?.confidence === "alta"
      ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"
      : detected?.confidence === "media"
      ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
      : "bg-slate-500/15 text-slate-300 border-slate-500/30";

  const adaptScore = adaptation?.score ?? null;
  const adaptCls =
    adaptScore == null
      ? "text-muted-foreground"
      : adaptScore >= 8
      ? "text-emerald-400"
      : adaptScore >= 5
      ? "text-amber-400"
      : "text-rose-400";

  return (
    <Card className="p-4 bg-card/80 border-indigo-500/30">
      <div className="flex flex-wrap items-start gap-3 justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-1">
            Código Interno do paciente
          </p>
          {detected ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-bold text-foreground">
                {detected.type_name}
              </span>
              <Badge className={cn("text-[10px] border", confColor)}>
                confiança {detected.confidence}
              </Badge>
              {detected.centro_inteligencia && (
                <Badge className="text-[10px] border bg-slate-500/15 text-slate-300 border-slate-500/30">
                  centro {detected.centro_inteligencia}
                </Badge>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Código não detectado</p>
          )}
          {detected?.crenca_central && (
            <p className="text-xs italic text-foreground/70 mt-1">
              Crença: "{detected.crenca_central}"
            </p>
          )}
        </div>
        {adaptScore != null && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Adaptação ao tipo
            </p>
            <p className={cn("text-2xl font-bold", adaptCls)}>{adaptScore}/10</p>
          </div>
        )}
      </div>

      {detected?.evidencia?.length ? (
        <div className="space-y-1 mb-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Sinais detectados
          </p>
          <ul className="space-y-1">
            {detected.evidencia.map((e, i) => (
              <li
                key={i}
                className="text-xs italic text-foreground/80 border-l-2 border-indigo-500/40 pl-2"
              >
                "{e}"
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detected?.como_decide && (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
            Como esse paciente decide
          </p>
          <p className="text-sm text-foreground/90">{detected.como_decide}</p>
        </div>
      )}

      {detected?.o_que_ele_precisava && (
        <div className="mb-3 p-2.5 rounded-md bg-indigo-500/10 border border-indigo-500/20">
          <p className="text-[10px] uppercase tracking-wider text-indigo-300 mb-0.5">
            O que esse paciente precisava
          </p>
          <p className="text-sm text-foreground">{detected.o_que_ele_precisava}</p>
        </div>
      )}

      {adaptation && (
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {adaptation.ajustes_corretos?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
                Ajustes corretos
              </p>
              <ul className="space-y-0.5">
                {adaptation.ajustes_corretos.map((a, i) => (
                  <li key={i} className="text-xs text-foreground/85 flex gap-1.5">
                    <CheckCircle className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {adaptation.ajustes_ausentes?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">
                Ajustes ausentes
              </p>
              <ul className="space-y-0.5">
                {adaptation.ajustes_ausentes.map((a, i) => (
                  <li key={i} className="text-xs text-foreground/85 flex gap-1.5">
                    <XCircle className="w-3 h-3 mt-0.5 text-rose-400 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {adaptation?.deal_killers_acionados && adaptation.deal_killers_acionados.length > 0 && (
        <div className="mb-2 p-2.5 rounded-md bg-rose-600/15 border border-rose-600/40">
          <p className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold mb-1">
            Deal-killers acionados pra esse Código
          </p>
          <ul className="space-y-0.5">
            {adaptation.deal_killers_acionados.map((k, i) => (
              <li key={i} className="text-xs text-rose-100 flex gap-1.5">
                <span className="text-rose-400 shrink-0">⚠</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {adaptation?.perguntas_banidas_detectadas && adaptation.perguntas_banidas_detectadas.length > 0 && (
        <div className="mb-2 p-2.5 rounded-md bg-rose-500/10 border border-rose-500/30">
          <p className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">
            Perguntas banidas detectadas
          </p>
          <ul className="space-y-0.5">
            {adaptation.perguntas_banidas_detectadas.map((q, i) => (
              <li key={i} className="text-xs italic text-rose-200">"{q}"</li>
            ))}
          </ul>
        </div>
      )}

      {adaptation?.conducao_descarrilamento?.houve_descarrilamento && (
        <div className="mb-2 p-2.5 rounded-md bg-purple-500/10 border border-purple-500/30">
          <p className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-1">
            Descarrilamento típico do tipo
          </p>
          <p className="text-xs text-foreground/90 mb-1">
            <span className="text-purple-300 font-medium">Como o profissional lidou:</span>{" "}
            {adaptation.conducao_descarrilamento.como_o_profissional_lidou}
          </p>
          <p className="text-xs text-foreground/90">
            <span className="text-purple-300 font-medium">Técnica oficial que caberia:</span>{" "}
            {adaptation.conducao_descarrilamento.tecnica_oficial_que_caberia}
          </p>
        </div>
      )}

      {adaptation?.tecnica_fechamento && (
        <div className={cn(
          "mb-2 p-2.5 rounded-md border",
          adaptation.tecnica_fechamento.match
            ? "bg-emerald-500/10 border-emerald-500/30"
            : "bg-amber-500/10 border-amber-500/30"
        )}>
          <p className={cn(
            "text-[10px] uppercase tracking-wider font-semibold mb-1",
            adaptation.tecnica_fechamento.match ? "text-emerald-400" : "text-amber-400"
          )}>
            Técnica de fechamento {adaptation.tecnica_fechamento.match ? "(match)" : "(mismatch)"}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Ideal pro tipo:</span>{" "}
              <span className="font-medium text-foreground capitalize">
                {adaptation.tecnica_fechamento.ideal_para_tipo.replace("_", "/")}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Usada:</span>{" "}
              <span className="font-medium text-foreground capitalize">
                {adaptation.tecnica_fechamento.usada_pelo_profissional.replace("_", "/")}
              </span>
            </div>
          </div>
        </div>
      )}

      {adaptation?.stage_adaptation_breakdown && (
        <div className="mb-2 p-2.5 rounded-md bg-indigo-500/5 border border-indigo-500/20">
          <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-2">
            Adaptação por etapa
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {(["conexao","perguntas_iniciais","investigacao_dor","diagnostico_clinico","oferta","fechamento"] as const).map((slug) => {
              const v = adaptation.stage_adaptation_breakdown![slug];
              const cls =
                v >= 8 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : v >= 5 ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                : "bg-rose-500/15 text-rose-300 border-rose-500/30";
              return (
                <div key={slug} className={cn("text-center rounded-md border px-1 py-1.5", cls)}>
                  <p className="text-[9px] uppercase tracking-wider opacity-70">
                    {ETAPA_SHORT[slug] ?? slug}
                  </p>
                  <p className="text-sm font-bold">{v}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {adaptation?.insight_personalizado && (
        <div className="p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">
            Insight personalizado
          </p>
          <p className="text-sm text-foreground">{adaptation.insight_personalizado}</p>
        </div>
      )}

      {adaptation?.frase_modelo && (
        <div className="mt-2 p-2.5 rounded-md bg-primary/5 border border-primary/30">
          <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1">
            Frase modelo pra esse Código
          </p>
          <p className="text-sm italic text-foreground">"{adaptation.frase_modelo}"</p>
        </div>
      )}
    </Card>
  );
}

// ─── Personalidade do Paciente ────────────────────────────────────────────────

const CODIGO_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  C1: { bg: "bg-slate-500/10",   border: "border-slate-500/30",   text: "text-slate-300" },
  C2: { bg: "bg-rose-500/10",    border: "border-rose-500/30",    text: "text-rose-300" },
  C3: { bg: "bg-amber-500/10",   border: "border-amber-500/30",   text: "text-amber-300" },
  C4: { bg: "bg-purple-500/10",  border: "border-purple-500/30",  text: "text-purple-300" },
  C5: { bg: "bg-blue-500/10",    border: "border-blue-500/30",    text: "text-blue-300" },
  C6: { bg: "bg-cyan-500/10",    border: "border-cyan-500/30",    text: "text-cyan-300" },
  C7: { bg: "bg-warning/10",  border: "border-warning/30",  text: "text-yellow-300" },
  C8: { bg: "bg-destructive/10",     border: "border-destructive/30",     text: "text-red-300" },
  C9: { bg: "bg-green-500/10",   border: "border-green-500/30",   text: "text-green-300" },
};

const CONFIANCA_LABEL: Record<string, string> = {
  alta: "Alta confiança",
  media: "Média confiança",
  baixa: "Baixa confiança",
};

const CENTRO_LABEL: Record<string, string> = {
  emocional: "Centro Emocional",
  mental: "Centro Mental",
  corporal: "Centro Corporal",
};

function PersonalidadePacienteBlock({ p }: { p: PersonalidadePaciente }) {
  const [expanded, setExpanded] = useState(false);
  const colors = CODIGO_COLORS[p.codigo] ?? CODIGO_COLORS.C1;

  return (
    <Card className="p-4 bg-card/80 border-border/50 overflow-hidden">
      <div className="flex items-start gap-3">
        <div className={cn("rounded-xl border px-3 py-2 shrink-0 text-center", colors.bg, colors.border)}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Código</p>
          <p className={cn("text-xl font-bold", colors.text)}>{p.codigo}</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-foreground">{p.nome}</h3>
            <Badge className={cn("text-[10px] border", colors.bg, colors.border, colors.text)}>
              {CONFIANCA_LABEL[p.confianca]}
            </Badge>
            {p.centro_inteligencia && (
              <span className="text-[10px] text-muted-foreground">{CENTRO_LABEL[p.centro_inteligencia]}</span>
            )}
          </div>
          {p.crenca_central && (
            <p className="text-xs text-muted-foreground italic mb-1">"{p.crenca_central}"</p>
          )}
          {p.insight && (
            <p className="text-xs text-foreground/80 leading-relaxed">{p.insight}</p>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors mt-0.5"
          aria-label={expanded ? "Recolher" : "Expandir"}
        >
          <CaretDown className={cn("w-4 h-4 transition-transform", expanded && "rotate-180")} />
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-border/30 pt-4">
          {p.indicios.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                Indícios na fala do paciente
              </p>
              <ul className="space-y-1.5">
                {p.indicios.map((ind, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                    <span className={cn("inline-block h-1.5 w-1.5 rounded-full shrink-0 mt-1", colors.text.replace("text-", "bg-"))} />
                    {ind}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {p.estilo_decisao && (
              <div className="bg-muted/20 rounded-lg p-3 border border-border/30">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">Estilo de decisão</p>
                <p className="text-xs text-foreground">{p.estilo_decisao}</p>
              </div>
            )}
            {p.ritmo_comunicacao && (
              <div className="bg-muted/20 rounded-lg p-3 border border-border/30">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">Ritmo de comunicação</p>
                <p className="text-xs text-foreground">{p.ritmo_comunicacao}</p>
              </div>
            )}
          </div>
          {p.o_que_precisava && (
            <div className="flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
              <Brain weight="duotone" className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">O que precisava</p>
                <p className="text-xs text-foreground leading-relaxed">{p.o_que_precisava}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Campo Semântico ──────────────────────────────────────────────────────────

const CARGA_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  alta:  { bg: "bg-primary/15", border: "border-primary/40", text: "text-primary" },
  media: { bg: "bg-muted/30",   border: "border-border/50",  text: "text-foreground" },
  baixa: { bg: "bg-muted/20",   border: "border-border/30",  text: "text-muted-foreground" },
};

const CATEGORIA_META = {
  dor:     { label: "Dor",     color: "text-rose-400",    bg: "bg-rose-500/10",   border: "border-rose-500/30" },
  desejo:  { label: "Desejo",  color: "text-emerald-400", bg: "bg-emerald-500/10",border: "border-emerald-500/30" },
  objecao: { label: "Objeção", color: "text-amber-400",   bg: "bg-amber-500/10",  border: "border-amber-500/30" },
} as const;

function TermoTag({ termo }: { termo: TermoSemantico }) {
  const [showExample, setShowExample] = useState(false);
  const carga = CARGA_COLORS[termo.carga_emocional] ?? CARGA_COLORS.media;

  return (
    <button
      onClick={() => setShowExample(!showExample)}
      className={cn(
        "inline-flex flex-col items-start gap-0.5 rounded-lg px-2.5 py-1.5 border text-left transition-all",
        carga.bg, carga.border,
        "hover:brightness-110 cursor-pointer"
      )}
    >
      <span className={cn("text-sm font-medium", carga.text)}>
        {termo.termo}
        {termo.frequencia > 1 && (
          <span className="ml-1 text-[10px] text-muted-foreground opacity-70">×{termo.frequencia}</span>
        )}
      </span>
      {showExample && termo.exemplo_frase && (
        <span className="text-[11px] text-muted-foreground italic leading-tight max-w-[200px]">
          "{termo.exemplo_frase}"
        </span>
      )}
    </button>
  );
}

const TIPO_OPTIONS: { value: RoteiroType; label: string }[] = [
  { value: "Reels",    label: "Reels" },
  { value: "Stories",  label: "Stories" },
  { value: "Anúncio",  label: "Anúncio" },
];

const OBJETIVO_OPTIONS: { value: ObjetivoRoteiro; label: string }[] = [
  { value: "educativo",   label: "Educativo" },
  { value: "engajamento", label: "Engajamento" },
  { value: "conversão",   label: "Conversão" },
  { value: "autoridade",  label: "Autoridade" },
];

function buildDefaultTema(cs: CampoSemantico): string {
  const central = cs.palavra_chave_central;
  const topDor = cs.dor.find(t => t.carga_emocional === "alta")?.termo;
  const topDesejo = cs.desejo.find(t => t.carga_emocional === "alta")?.termo;

  if (topDesejo && central && topDesejo.toLowerCase() !== central.toLowerCase()) {
    return `Como alcançar ${topDesejo} e resolver ${central}`;
  }
  if (topDor && central && topDor.toLowerCase() !== central.toLowerCase()) {
    return `${central}: como resolver ${topDor} de vez`;
  }
  if (central) {
    return `Como resolver ${central} definitivamente`;
  }
  if (topDesejo) return `Como alcançar ${topDesejo}`;
  if (topDor) return `Como resolver ${topDor}`;
  return "";
}

function buildCampoSemanticoContext(cs: CampoSemantico): string {
  const parts: string[] = [];
  if (cs.palavra_chave_central) {
    parts.push(`Palavra central do paciente: "${cs.palavra_chave_central}"`);
  }
  const dorTermos = cs.dor.filter(t => t.carga_emocional === "alta").slice(0, 5).map(t => t.termo);
  if (dorTermos.length) parts.push(`Dores principais: ${dorTermos.join(", ")}`);
  const desejosTermos = cs.desejo.filter(t => t.carga_emocional === "alta").slice(0, 5).map(t => t.termo);
  if (desejosTermos.length) parts.push(`Desejos principais: ${desejosTermos.join(", ")}`);
  const objecaoTermos = cs.objecao.slice(0, 3).map(t => t.termo);
  if (objecaoTermos.length) parts.push(`Objeções: ${objecaoTermos.join(", ")}`);
  if (cs.expressoes_marcantes.length) {
    parts.push(`Frases marcantes do paciente:\n${cs.expressoes_marcantes.map(e => `- "${e}"`).join("\n")}`);
  }
  return parts.join("\n\n");
}

function CampoSemanticoRoteiroForm({ cs }: { cs: CampoSemantico }) {
  const [tipo, setTipo] = useState<RoteiroType>("Reels");
  const [objetivo, setObjetivo] = useState<ObjetivoRoteiro>("conversão");
  const [tema, setTema] = useState(() => buildDefaultTema(cs));
  const [success, setSuccess] = useState<string | null>(null);
  const { mutate, isPending, error } = useGenerateRoteiro();

  // BYOK: surface CTA toast when the user has no provider key configured.
  useEffect(() => {
    if (error && isNoConfiguredProviderError(error)) {
      showNoConfiguredProviderToast();
    }
  }, [error]);

  function handleGenerate() {
    const campoContext = buildCampoSemanticoContext(cs);
    mutate(
      {
        topic: tema,
        type: tipo,
        objetivo,
        details: `[CAMPO SEMÂNTICO DO PACIENTE — use estas palavras e expressões exatas no roteiro]\n\n${campoContext}`,
      },
      {
        onSuccess: (data) => setSuccess(data.jobId),
      }
    );
  }

  if (success) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5 text-sm text-emerald-400">
        <CheckCircle weight="fill" className="w-4 h-4 shrink-0" />
        Roteiro pronto.{" "}
        <a href={`/roteiros/${success}`} className="underline hover:brightness-125 font-medium">
          Abrir agora
        </a>
        <span className="text-emerald-400/60 mx-1">·</span>
        <a href="/roteiros" className="underline text-emerald-400/80 hover:brightness-125">
          Ver todos
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1 font-medium">Tema</label>
          <input
            value={tema}
            onChange={(e) => setTema(e.target.value)}
            placeholder="Tema do roteiro..."
            className="w-full rounded-lg border border-border/50 bg-muted/20 px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1 font-medium">Formato</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as RoteiroType)}
            className="rounded-lg border border-border/50 bg-muted/20 px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1 font-medium">Objetivo</label>
          <select
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value as ObjetivoRoteiro)}
            className="rounded-lg border border-border/50 bg-muted/20 px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            {OBJETIVO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/20 rounded-lg px-3 py-2 border border-border/30">
        <span className="shrink-0">🔒</span>
        Campo semântico travado como contexto — o roteiro usará as palavras exatas do paciente.
      </div>
      {error && (
        <p className="text-xs text-rose-400">
          {isNoConfiguredProviderError(error)
            ? "Conecte uma chave de IA em Perfil > Conexões para gerar este roteiro."
            : error.message}
        </p>
      )}
      <Button
        onClick={handleGenerate}
        disabled={isPending || !tema.trim()}
        size="sm"
        className="gap-2"
      >
        {isPending
          ? <><Spinner className="w-3.5 h-3.5 animate-spin" /> Gerando roteiro...</>
          : <><ArrowRight className="w-3.5 h-3.5" /> Gerar roteiro com campo semântico</>
        }
      </Button>
    </div>
  );
}

function CampoSemanticoBlock({ cs }: { cs: CampoSemantico }) {
  const [showRoteiro, setShowRoteiro] = useState(false);
  const hasTermos = cs.dor.length > 0 || cs.desejo.length > 0 || cs.objecao.length > 0;

  return (
    <Card className="p-4 bg-card/80 border-border/50 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Quotes weight="duotone" className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Campo Semântico do Paciente</h3>
        </div>
        {cs.palavra_chave_central && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30">
            <span className="text-[10px] text-muted-foreground">Central:</span>
            <span className="text-xs font-semibold text-primary">{cs.palavra_chave_central}</span>
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground -mt-2">
        Linguagem nativa do paciente — use estas palavras na sua comunicação para máxima conexão. Clique num termo para ver exemplo na fala.
      </p>

      {/* Categorias */}
      {hasTermos && (
        <div className="space-y-3">
          {(["dor", "desejo", "objecao"] as const).map((cat) => {
            const termos = cs[cat];
            if (!termos.length) return null;
            const meta = CATEGORIA_META[cat];
            return (
              <div key={cat}>
                <p className={cn("text-[10px] uppercase tracking-wider font-semibold mb-2", meta.color)}>
                  {meta.label}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {termos
                    .sort((a, b) => {
                      const order = { alta: 0, media: 1, baixa: 2 };
                      return (order[a.carga_emocional] ?? 1) - (order[b.carga_emocional] ?? 1);
                    })
                    .map((t, i) => <TermoTag key={i} termo={t} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expressões marcantes */}
      {cs.expressoes_marcantes.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Expressões marcantes
          </p>
          <div className="space-y-2">
            {cs.expressoes_marcantes.map((expr, i) => (
              <div key={i} className="flex items-start gap-2 border-l-2 border-primary/30 pl-3">
                <p className="text-sm italic text-foreground/80">"{expr}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roteiro generator */}
      <div className="border-t border-border/30 pt-4">
        <button
          onClick={() => setShowRoteiro(!showRoteiro)}
          className={cn(
            "w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 border transition-all",
            showRoteiro
              ? "bg-primary/10 border-primary/40 text-primary"
              : "bg-primary/5 border-primary/20 text-primary hover:bg-primary/15 hover:border-primary/40 hover:shadow-sm hover:shadow-primary/10"
          )}
        >
          <div className="flex items-center gap-2.5">
            <Lightning weight="fill" className="w-4 h-4 shrink-0" />
            <div className="text-left">
              <p className="text-sm font-semibold leading-tight">
                {showRoteiro ? "Fechar gerador de roteiro" : "Criar roteiro com este campo semântico"}
              </p>
              {!showRoteiro && (
                <p className="text-[11px] text-primary/70 font-normal">
                  Gera usando as palavras exatas do paciente
                </p>
              )}
            </div>
          </div>
          <CaretDown className={cn("w-4 h-4 shrink-0 transition-transform", showRoteiro && "rotate-180")} />
        </button>
        {showRoteiro && (
          <div className="mt-3">
            <CampoSemanticoRoteiroForm cs={cs} />
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── RenderV4 ─────────────────────────────────────────────────────────────────

function RenderV4({ data }: { data: ConsultaReport }) {
  const p = data;
  const score = p.score_final ?? Math.round((p.method_total / 60) * 100);
  const gradeMeta = p.grade ? GRADE_META[p.grade] : null;
  const critSlug = p.etapa_critica;

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-card/80 border border-border/50 rounded-xl">
        <div className="flex flex-col items-center rounded-xl border border-primary/30 px-4 py-3 bg-primary/10">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Score</span>
          <span className="text-3xl font-bold text-primary">{score}</span>
          <span className="text-[10px] text-muted-foreground">de 100</span>
        </div>
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            Método: <span className="font-semibold text-foreground">{p.method_total ?? 0}/60</span>
          </div>
          {gradeMeta && (
            <Badge className={cn("border font-semibold", gradeMeta.cls)}>
              {gradeMeta.label}
            </Badge>
          )}
          {p.resultado && (
            <div className="text-xs text-muted-foreground capitalize">{p.resultado.replace(/_/g, " ")}</div>
          )}
        </div>
        {p.resumo_executivo && (
          <div className="flex-1 min-w-0 bg-muted/20 border border-border/30 rounded-lg p-3">
            <p className="text-sm text-foreground leading-relaxed">{p.resumo_executivo}</p>
          </div>
        )}
      </div>

      {/* Personalidade do paciente (Código Interno detectado da fala) */}
      {p.personalidade_paciente && (
        <PersonalidadePacienteBlock p={p.personalidade_paciente} />
      )}

      {/* Eneatipo detectado + adaptação */}
      <EneatypeBlock
        detected={p.eneatype_detected}
        adaptation={p.eneatype_adaptation}
      />

      {/* palavra_emocional_central */}
      {p.palavra_emocional_central && (
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
          <span className="text-xs text-violet-400 font-medium">Palavra-chave:</span>
          <span className="text-sm font-semibold text-violet-300">{p.palavra_emocional_central}</span>
        </div>
      )}

      {/* nivel_dor_maximo */}
      {p.nivel_dor_maximo && (
        <Card className="p-4 bg-card/80 border-border/50">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">
            Nível de dor atingido
          </p>
          <p className="text-sm font-semibold text-foreground">
            Nível {p.nivel_dor_maximo.nivel} —{" "}
            {["", "Fato", "Sentimento", "Privação"][p.nivel_dor_maximo.nivel]}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{p.nivel_dor_maximo.descricao}</p>
          {p.nivel_dor_maximo.evidencia && (
            <p className="text-xs italic text-foreground/70 mt-1 border-l-2 border-border/40 pl-2">
              "{p.nivel_dor_maximo.evidencia}"
            </p>
          )}
        </Card>
      )}

      {/* Flags críticas */}
      {p.flags_criticas?.some(f => f.detectada) && (
        <FlagsBlock flags={p.flags_criticas} />
      )}

      {/* Gargalo */}
      {p.gargalo_principal && (
        <GargaloBlock gargalo={p.gargalo_principal} />
      )}

      {/* Ações corretivas */}
      {p.acoes_corretivas?.length > 0 && (
        <AcoesBlock acoes={p.acoes_corretivas} />
      )}

      {/* macro_cs */}
      {p.macro_cs && (
        <Card className="p-4 bg-card/80 border-border/50">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Macro CS</h3>
          <div className="grid grid-cols-3 gap-3">
            {(["conexao", "conducao", "conversao"] as const).map((key) => {
              const item = p.macro_cs![key];
              if (!item) return null;
              const statusCls = item.status === "executado"
                ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                : item.status === "parcial"
                ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
                : "text-rose-400 bg-rose-500/10 border-rose-500/30";
              const label = { conexao: "Conexão", conducao: "Condução", conversao: "Conversão" }[key];
              return (
                <div key={key} className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                  <p className="text-lg font-bold text-foreground">{item.score}</p>
                  <Badge className={cn("text-[10px] border", statusCls)}>{item.status}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 6 Etapas */}
      <Card className="p-4 bg-card/80 border-border/50">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Método Influência Máxima — 6 Etapas
        </h3>
        <div className="space-y-2">
          {ETAPA_ORDER_V4.map((slug, i) => {
            const stageEval = p.method_stages?.[slug];
            if (!stageEval) return null;
            return (
              <StageRowV4
                key={slug}
                slug={slug}
                label={ETAPA_LABELS_V4[slug]}
                stageEval={stageEval}
                isCritical={critSlug === slug}
                index={i}
              />
            );
          })}
        </div>
      </Card>

      {/* Narrative */}
      <NarrativeSection
        title="O que foi bem"
        items={p.strengths}
        tone="emerald"
        icon={CheckCircle}
      />
      <NarrativeSection
        title="O que pode melhorar"
        items={p.improvements}
        tone="amber"
        icon={Warning}
      />
      {p.recommendations?.length ? (
        <NarrativeSection
          title="Próximas vezes"
          items={p.recommendations}
          tone="primary"
          icon={Lightning}
        />
      ) : null}

      {/* Campo Semântico */}
      {p.campo_semantico && (
        <CampoSemanticoBlock cs={p.campo_semantico} />
      )}

      {/* Pratica */}
      {p.pratica && (
        <Card className="p-4 bg-card/80 border-2 border-primary/40">
          <header className="mb-3 flex items-center gap-2">
            <Lightning weight="duotone" className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Plano de Prática</h3>
          </header>
          <div className="space-y-3">
            {p.pratica.treinar.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">Treinar</p>
                <ul className="space-y-1.5">
                  {p.pratica.treinar.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.pratica.foco_proxima_sessao && (
              <div className="flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
                <Lightning weight="bold" className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Foco da próxima sessão
                  </p>
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {p.pratica.foco_proxima_sessao}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── V3 legacy render ─────────────────────────────────────────────────────────

function RenderV3Legacy({ data }: { data: AnalysisV3Legacy }) {
  return (
    <div className="space-y-3">
      {/* Legacy banner */}
      <div className="text-xs text-muted-foreground bg-muted/30 border border-border/30 rounded-lg px-3 py-2">
        Análise gerada antes da atualização v4. Reprocesse para diagnóstico completo.
      </div>

      {/* Score */}
      <section className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-primary">{data.score_total}</span>
          {data.classificacao && (
            <span className="text-sm text-muted-foreground">{String(data.classificacao)}</span>
          )}
        </div>
      </section>

      {/* Etapas */}
      {data.etapas && typeof data.etapas === "object" && (
        <section className="bg-secondary/20 border border-border/40 rounded-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Etapas</h4>
          <div className="space-y-2">
            {Object.entries(data.etapas).map(([etapaKey, etapaVal]) => (
              <div key={etapaKey} className="text-sm text-foreground">
                <span className="font-medium">{etapaKey}: </span>
                <span className="text-muted-foreground">{JSON.stringify(etapaVal)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Clinical legacy render ───────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-secondary/20 border border-border/40 rounded-lg p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </h4>
      <div className="text-sm text-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function RenderClinical({ data }: { data: ClinicalLegacy }) {
  return (
    <div className="space-y-3">
      {data.resumo_executivo && (
        <section className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Resumo</h4>
          <p className="text-sm text-foreground leading-relaxed">{data.resumo_executivo}</p>
        </section>
      )}
      {data.queixa_principal && (
        <Section title="Queixa Principal"><p>{data.queixa_principal}</p></Section>
      )}
      {data.historico_relevante && (
        <Section title="Histórico Relevante"><p>{data.historico_relevante}</p></Section>
      )}
      {data.exame_fisico && (
        <Section title="Exame Físico"><p>{data.exame_fisico}</p></Section>
      )}
      {data.hipoteses_diagnosticas && data.hipoteses_diagnosticas.length > 0 && (
        <Section title="Hipóteses Diagnósticas">
          <ul className="list-disc pl-5 space-y-1">
            {data.hipoteses_diagnosticas.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </Section>
      )}
      {data.plano_terapeutico && data.plano_terapeutico.length > 0 && (
        <Section title="Plano Terapêutico">
          <ul className="list-disc pl-5 space-y-1">
            {data.plano_terapeutico.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </Section>
      )}
      {data.alertas_clinicos && data.alertas_clinicos.length > 0 && (
        <section className="bg-warning/5 border-l-4 border-warning rounded-r-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-yellow-400 mb-2">
            Alertas Clínicos
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm text-foreground">
            {data.alertas_clinicos.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function ConsultaAnalysisView({ analysis }: { analysis: unknown }) {
  // Accept either the raw string OR the wrapper { analysis: "..." }
  let inner: unknown = analysis;
  if (analysis && typeof analysis === "object" && "analysis" in (analysis as Record<string, unknown>)) {
    inner = (analysis as Record<string, unknown>).analysis;
  }

  const parsed = tryParse(inner);

  if (!parsed) {
    return (
      <pre className="text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed bg-secondary/30 rounded-lg p-4">
        {typeof inner === "string" ? inner : JSON.stringify(analysis, null, 2)}
      </pre>
    );
  }

  if (parsed.kind === "error") {
    return (
      <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
        <p className="text-sm font-medium text-destructive">Análise não pôde ser concluída</p>
        <p className="text-xs text-muted-foreground mt-1">
          {parsed.data.mensagem || parsed.data.erro}
        </p>
      </div>
    );
  }

  if (parsed.kind === "v4") {
    return <RenderV4 data={parsed.data} />;
  }

  if (parsed.kind === "v3") {
    return <RenderV3Legacy data={parsed.data} />;
  }

  return <RenderClinical data={parsed.data} />;
}
